import type { Transaction } from '@elastic/apm-rum'
import { createSharedComposable, tryOnScopeDispose } from '@vueuse/core'

export type ApmLabels = Record<string, string | number | boolean>
export type ApmLabelProvider = () => ApmLabels | null | undefined

interface UseApmLabelsReturn {
    register: (provider: ApmLabelProvider) => void
    stamp: (transaction: Transaction) => void
}

/**
 * Generic APM label registry. Features register a provider that returns the labels for
 * the current context (or null); the APM plugin stamps every transaction with the merged
 * result. Nothing is written to the agent's global context, so labels disappear as soon
 * as a provider stops returning them (e.g. when its owning view's scope disposes).
 */
export const useApmLabels = createSharedComposable((): UseApmLabelsReturn => {
    const providers = new Set<ApmLabelProvider>()

    const register = (provider: ApmLabelProvider): void => {
        providers.add(provider)
        tryOnScopeDispose(() => {
            providers.delete(provider)
        })
    }

    const stamp = (transaction: Transaction): void => {
        if (typeof transaction.addLabels !== 'function') return
        let merged: ApmLabels | undefined
        for (const provide of providers) {
            const labels = provide()
            if (labels) merged = merged ? { ...merged, ...labels } : labels
        }
        if (merged) transaction.addLabels(merged)
    }

    return { register, stamp }
})
